import { PageWrapper, SidebarLayout } from "~/components/SidebarLayout";
import { Navbar } from "~/components/Navbar/component";
import { UserFetcher } from "~/modules/user/components/UserFetcher";
import { NextSeo } from "next-seo";
import { PageTrack } from "~/modules/analytics/components/PageTrack";
import { Routes } from "~/routes";
import { Asset, AssetColumns } from "~/modules/assets/types";
import {
    Filter,
    FilterOperators,
    QueryResponse,
    SortDirections,
    FilterMetadata,
} from "~/modules/filters/types";
import { queryAssetFilterMeta, queryAssets } from "~/modules/assets/api/router";
import {
    LeagueTableGroupTypes,
    LeagueTableTabsByDistributionType,
} from "~/modules/league_tables/types";
import { NEXTJS_REVALIDATE_TIMEOUT } from "~/server/consts";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import { AssetClassPageSEO } from "~/modules/app/seo/AssetClassPageSEO";
import { BreadcrumbSEO } from "~/modules/app/seo/BreadcrumbSEO";
import {
    NetFlowsData,
    queryNetFlows,
} from "~/modules/fund_flows/api/queryNetFlows";
import { AssetClassIndividualPage } from "~/modules/asset_classes/pages/individual";
import {
    DISTRIBUTION_TYPE_AGGREGATE_TOOLTIP_LINK_TO,
    DISTRIBUTION_TYPE_TOOLTIPS,
    MEASURE_IDS,
    SOCIAL_OG_IMAGE_URL,
    TOKENIZATION_TYPE_IDS,
} from "~/consts";
import {
    TokenTimeseriesColumns,
    TokenTimeseriesGroupBy,
} from "~/modules/token_timeseries/types";
import { Props as AggregateDisplayProps } from "~/components/AggregateDisplay";
import { AggregateValueTypes } from "~/components/AggregateDisplay/types";
import { queryTokenAggregateValueWith30D } from "~/modules/token_timeseries/api/queryTokenAggregateValue";
import { calculatePercentChange } from "~/utils/calculatePercentChange";
import { collectAllQueryResults } from "~/utils/collectAllQueryResults";
import {
    ASSET_CLASS_DESCRIPTIONS,
    ASSET_CLASS_PAGE_TO_SORT,
    ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS,
    ASSET_CLASS_PAGES_TO_FILTER,
} from "~/modules/asset_classes/consts";
import { ColumnMetadata } from "~/components/DataGrid";
import { AssetTableRowData } from "~/modules/assets/components/AssetTable/types";
import { NETWORKS_COLUMN } from "~/modules/assets/components/AssetTable/columns/Networks";
import { HOLDING_ADDRESS_COUNT_COLUMN } from "~/modules/assets/components/AssetTable/columns/Holders";
import { LICENSE_OR_REGULATORY_FRAMEWORK } from "~/modules/assets/components/AssetTable/columns/RegulatoryFramework";
import { ASSET_NAME_AND_TICKER_COLUMN_EXPANDABLE } from "~/modules/assets/components/AssetTable/columns/AssetName";
import { getTimeseriesModes } from "~/modules/visualization/components/Timeseries/getTimeseriesModes";
import {
    REAL_ESTATE_LOCATION_COLUMN,
    REAL_ESTATE_ASSET_TYPE_COLUMN,
    REAL_ESTATE_PROPERTY_TYPE_COLUMN,
    REAL_ESTATE_STRATEGY_COLUMN,
} from "~/modules/assets/components/AssetTable/columns/RealEstate";
import { CONTRACT_ADDRESS_COLUMN } from "~/modules/assets/components/AssetTable/columns/ContractAddress";
import { PLATFORM_COLUMN } from "~/modules/assets/components/AssetTable/columns/Platform";
import { TOTAL_ASSET_VALUE_COLUMN } from "~/modules/assets/components/AssetTable/columns/TotalAssetValue";
import { NET_ASSET_VALUE_COLUMN } from "~/modules/assets/components/AssetTable/columns/NetAssetValue";
import { TOKENIZATION_TYPE_COLUMN } from "~/modules/assets/components/AssetTable/columns/TokenizationType";
import { queryLeagueTablesByDistributionType } from "~/modules/league_tables/api/queryLeagueTablesByDistributionType";

// // // //

const SEO_TITLE = "Tokenized Real Estate";
const SEO_DESCRIPTION = ASSET_CLASS_DESCRIPTIONS.real_estate;

// // // //

const REAL_ESTATE_TABLE_COLUMNS: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
>[] = [
    ASSET_NAME_AND_TICKER_COLUMN_EXPANDABLE,
    TOTAL_ASSET_VALUE_COLUMN,
    NET_ASSET_VALUE_COLUMN,
    HOLDING_ADDRESS_COUNT_COLUMN,
    TOKENIZATION_TYPE_COLUMN,
    PLATFORM_COLUMN,
    NETWORKS_COLUMN,
    REAL_ESTATE_LOCATION_COLUMN,
    REAL_ESTATE_ASSET_TYPE_COLUMN,
    REAL_ESTATE_PROPERTY_TYPE_COLUMN,
    REAL_ESTATE_STRATEGY_COLUMN,
    LICENSE_OR_REGULATORY_FRAMEWORK,
    CONTRACT_ADDRESS_COLUMN,
];

// // // //

interface AggregateValues {
    marketCapSum: number;
    marketCapPercentChange: number;
    totalTransferVolume: number;
    totalTransferVolumePercentChange: number;
    holders: number;
    holdersPercentChange: number;
    totalActiveAddresses: number;
    totalActiveAddressesPercentChange: number;
}

function getRealEstateAggregates(assets: Asset[]): AggregateValues {
    const marketCapSum = assets.reduce(
        (acc, asset) => acc + (asset.total_asset_value_dollar?.val ?? 0),
        0,
    );
    const marketCapSum30DaysAgo = assets.reduce(
        (acc, asset) => acc + (asset.total_asset_value_dollar?.val_30d ?? 0),
        0,
    );
    const totalTransferVolume = assets.reduce(
        (acc, asset) => acc + (asset.trailing_30_day_transfer_volume?.val ?? 0),
        0,
    );
    const totalTransferVolume30DaysAgo = assets.reduce(
        (acc, asset) =>
            acc + (asset.trailing_30_day_transfer_volume?.val_30d ?? 0),
        0,
    );
    const holders = assets.reduce(
        (acc, asset) => acc + (asset.holding_addresses_count?.val ?? 0),
        0,
    );
    const holders30DaysAgo = assets.reduce(
        (acc, asset) => acc + (asset.holding_addresses_count?.val_30d ?? 0),
        0,
    );
    const totalActiveAddresses = assets.reduce(
        (acc, asset) =>
            acc + (asset.trailing_30_day_active_addresses_count?.val ?? 0),
        0,
    );
    const totalActiveAddresses30DaysAgo = assets.reduce(
        (acc, asset) =>
            acc + (asset.trailing_30_day_active_addresses_count?.val_30d ?? 0),
        0,
    );
    const marketCapPercentChange = calculatePercentChange({
        initialValue: marketCapSum30DaysAgo,
        finalValue: marketCapSum,
    });
    const totalTransferVolumePercentChange = calculatePercentChange({
        initialValue: totalTransferVolume30DaysAgo,
        finalValue: totalTransferVolume,
    });
    const holdersPercentChange = calculatePercentChange({
        initialValue: holders30DaysAgo,
        finalValue: holders,
    });
    const totalActiveAddressesPercentChange = calculatePercentChange({
        initialValue: totalActiveAddresses30DaysAgo,
        finalValue: totalActiveAddresses,
    });
    return {
        marketCapSum,
        totalTransferVolume,
        holders,
        marketCapPercentChange,
        totalTransferVolumePercentChange,
        holdersPercentChange,
        totalActiveAddresses,
        totalActiveAddressesPercentChange,
    };
}

// Page size used when collecting every real estate asset for aggregate calculations
const REAL_ESTATE_ALL_ASSETS_PER_PAGE = 500;

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation is enabled and a new request comes in
// NOTE - this will have to be changed to hit api.rwa.xyz when the API has been updated
export async function getStaticProps() {
    // Base filters for all real estate queries
    const baseFilters: Filter[] =
        ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS.real_estate;

    // Make all requests in parallel + destructure responses
    const [
        listQueryResponse,
        allRealEstateAssets,
        filterMetadata,
        netFlowsData,
        leagueTableTabs,
        distributedAggregates,
        representedAggregates,
    ] = await Promise.all([
        queryAssets({
            query: {
                filter: ASSET_CLASS_PAGES_TO_FILTER.real_estate,
                sort: ASSET_CLASS_PAGE_TO_SORT.real_estate,
                pagination: {
                    page: 1,
                    perPage: 25,
                },
            },
        }),
        // NOTE - the holder, active address, and country aggregates cover every
        // real estate asset, so page through all of them instead of only the table's first page
        collectAllQueryResults({
            perPage: REAL_ESTATE_ALL_ASSETS_PER_PAGE,
            query: {
                filter: ASSET_CLASS_PAGES_TO_FILTER.real_estate,
                sort: ASSET_CLASS_PAGE_TO_SORT.real_estate,
                pagination: {
                    page: 1,
                    perPage: REAL_ESTATE_ALL_ASSETS_PER_PAGE,
                },
                // NOTE - only grab the fields we need to calculate the aggregate values displayed at the top of the page
                // ENHANCEMENT - move all the aggregate calcuations into the API
                fields: [
                    "id",
                    "name",
                    "ticker",
                    "holding_addresses_count",
                    "total_asset_value_dollar",
                    "trailing_30_day_active_addresses_count",
                    "trailing_30_day_transfer_volume",
                    "real_estate_property",
                ],
            },
            queryResults: (query) => queryAssets({ query }),
        }),
        queryAssetFilterMeta({
            filter: ASSET_CLASS_PAGES_TO_FILTER.real_estate,
        }),
        queryNetFlows({
            name: "asset-class--real-estate",
            filters: ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS.real_estate,
            groupBy: TokenTimeseriesGroupBy.asset,
            limit: 10,
        }),
        queryLeagueTablesByDistributionType({
            name: "asset-class--real-estate",
            filter: {
                operator: FilterOperators.And,
                filters: baseFilters,
            },
            groups: [
                LeagueTableGroupTypes.platforms,
                LeagueTableGroupTypes.networks,
                LeagueTableGroupTypes.realEstateCountries,
            ],
            labelOverrides: {
                [LeagueTableGroupTypes.realEstateCountries]: {
                    key: LeagueTableGroupTypes.realEstateCountries,
                    label: "Countries",
                    groupColumnLabel: "Country",
                    enableEntityLinks: false,
                },
            },
        }),
        // Distributed Asset Value
        queryTokenAggregateValueWith30D({
            name: "asset-class--real-estate--distributed-asset-value",
            measureID: MEASURE_IDS.BridgedTokenValueDollar,
            filters: [
                ...baseFilters,
                {
                    field: TokenTimeseriesColumns.tokenizationTypeID,
                    operator: FilterOperators.Equals,
                    value: TOKENIZATION_TYPE_IDS.OnChainDistributed,
                },
            ],
        }),
        // Represented Asset Value
        queryTokenAggregateValueWith30D({
            name: "asset-class--real-estate--represented-asset-value",
            measureID: MEASURE_IDS.BridgedTokenValueDollar,
            filters: [
                ...baseFilters,
                {
                    field: TokenTimeseriesColumns.tokenizationTypeID,
                    operator: FilterOperators.Equals,
                    value: TOKENIZATION_TYPE_IDS.OnChainRepresented,
                },
            ],
        }),
    ]);

    // // // //

    const aggregateValues = getRealEstateAggregates(
        allRealEstateAssets.results,
    );

    const aggregates: Array<AggregateDisplayProps> = [
        {
            label: "Distributed Value",
            value: distributedAggregates.value,
            type: AggregateValueTypes.dollarCompact,
            tooltip: DISTRIBUTION_TYPE_TOOLTIPS.distributed,
            tooltipLinkTo: DISTRIBUTION_TYPE_AGGREGATE_TOOLTIP_LINK_TO,
            percentChange: {
                value: distributedAggregates.change30D,
                interval: "30d",
            },
        },
        {
            label: "Represented Value",
            value: representedAggregates.value,
            type: AggregateValueTypes.dollarCompact,
            tooltip: DISTRIBUTION_TYPE_TOOLTIPS.represented,
            tooltipLinkTo: DISTRIBUTION_TYPE_AGGREGATE_TOOLTIP_LINK_TO,
            percentChange: {
                value: representedAggregates.change30D,
                interval: "30d",
            },
        },
        {
            label: "Holders",
            tooltip:
                "The number of addresses that hold tokenized real estate tokens.",
            type: "count_compact",
            value: aggregateValues.holders,
            percentChange: {
                value: aggregateValues.holdersPercentChange,
                interval: "30d" as const,
            },
        },
        {
            label: "Monthly Active Addresses",
            tooltip:
                "Number of on-chain addresses that have transferred a tracked asset in the past 30 days.",
            type: "count_compact",
            value: aggregateValues.totalActiveAddresses,
            percentChange: {
                value: aggregateValues.totalActiveAddressesPercentChange,
                interval: "30d" as const,
            },
        },
        {
            label: "Assets",
            tooltip: "Total number of tracked tokenized real estate assets.",
            type: "count",
            value: listQueryResponse.pagination.resultCount,
        },
        {
            label: "Countries",
            tooltip: "Number of countries with tokenized real estate assets.",
            type: "count",
            value: new Set(
                allRealEstateAssets.results
                    .map((a) => a.real_estate_property?.country?.id)
                    .filter(Boolean),
            ).size,
        },
    ];

    // Trim down FilterMetadata options to only include the ones we need
    const trimmedFilterMetadata = filterMetadata.filter((each) => {
        return [
            AssetColumns.networkIDs,
            AssetColumns.protocolIDs,
            AssetColumns.tokenizationTypeIDs,
        ].includes(each.field as AssetColumns);
    });

    return {
        props: {
            listQueryResponse,
            filterMetadata: trimmedFilterMetadata,
            leagueTableTabs,
            aggregates,
            netFlows: netFlowsData,
        },
        // Next.js will re-generate this page based on NEXTJS_REVALIDATE_TIMEOUT const
        revalidate: NEXTJS_REVALIDATE_TIMEOUT,
    };
}

export default function RealEstatePage(props: {
    listQueryResponse: QueryResponse<Asset>;
    filterMetadata: FilterMetadata[];
    aggregates: Array<AggregateDisplayProps>;
    leagueTableTabs: LeagueTableTabsByDistributionType;
    netFlows: NetFlowsData;
}) {
    const {
        leagueTableTabs,
        listQueryResponse,
        filterMetadata,
        netFlows,
        aggregates,
    } = props;

    const modes = getTimeseriesModes({
        analyticsKey: "real-estate",
        defaultGroupBy: TokenTimeseriesGroupBy.asset,
        filters: ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS.real_estate,
    });

    return (
        <PageTrack route={Routes.realEstate}>
            <NextSeo
                title={SEO_TITLE}
                description={SEO_DESCRIPTION}
                canonical={addBaseUrl(Routes.realEstate)}
                openGraph={{
                    type: "website",
                    url: addBaseUrl(Routes.realEstate),
                    title: SEO_TITLE,
                    site_name: "RWA.xyz",
                    description: SEO_DESCRIPTION,
                    images: [
                        {
                            url: SOCIAL_OG_IMAGE_URL,
                            width: 4000,
                            height: 2300,
                            alt: "rwa.xyz",
                        },
                    ],
                }}
                additionalMetaTags={[
                    {
                        name: "keywords",
                        content:
                            "tokenized real estate, digital real estate, fractional ownership, tokenized REIT, property tokens, real world assets",
                    },
                ]}
            />
            <AssetClassPageSEO
                route={Routes.realEstate}
                description={SEO_DESCRIPTION}
            />
            <BreadcrumbSEO
                items={[
                    {
                        name: SEO_TITLE,
                        href: Routes.realEstate,
                    },
                ]}
            />
            <UserFetcher>
                {({ user }) => <Navbar route={Routes.realEstate} user={user} />}
            </UserFetcher>
            <SidebarLayout pathname={Routes.realEstate}>
                <PageWrapper>
                    <AssetClassIndividualPage
                        options={{
                            isRealEstate: true,
                        }}
                        listQueryResponse={listQueryResponse}
                        netFlows={{
                            title: "Tokenized Real Estate Net Flows",
                            data: netFlows,
                        }}
                        title={SEO_TITLE}
                        subtitle={SEO_DESCRIPTION}
                        assetsQuery={{
                            filter: ASSET_CLASS_PAGES_TO_FILTER.real_estate,
                            sort: ASSET_CLASS_PAGE_TO_SORT.real_estate,
                            pagination: {
                                page: 1,
                                perPage: 25,
                            },
                        }}
                        header={{
                            label: "Tokenized Real Estate",
                            loading: false,
                            value: `${listQueryResponse.pagination.resultCount.toLocaleString()} Total`,
                        }}
                        aggregates={aggregates}
                        timeseriesChart={{
                            title: "Tokenized Real Estate Metrics",
                            modes,
                            distributionSelect: {
                                distributed: {
                                    enabled: true,
                                },
                                represented: {
                                    enabled: true,
                                },
                                stablecoins: {
                                    enabled: false,
                                    active: false,
                                },
                            },
                            columnSpanConfig: {
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 12,
                                xxl: 12,
                            },
                        }}
                        pieChart={{
                            title: "Tokenized Real Estate Value",
                            leagueTableData: leagueTableTabs,
                            defaultGroupBy: LeagueTableGroupTypes.platforms,
                            allowedGroupByValues: [
                                LeagueTableGroupTypes.platforms,
                                LeagueTableGroupTypes.networks,
                                LeagueTableGroupTypes.realEstateCountries,
                            ],
                            colSpans: {
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 6,
                                xxl: 6,
                            },
                        }}
                        leagueTable={{
                            leagueTableData: leagueTableTabs,
                            analyticsTableName: "real-estate-league-table",
                            title: "Tokenized Real Estate League Table",
                            colSpans: {
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 6,
                                xxl: 6,
                            },
                        }}
                        table={{
                            filterMetadata,
                            columns: REAL_ESTATE_TABLE_COLUMNS,
                            analyticsTableName: "real-estate",
                            exportFilename: "real-estate-assets",
                            includeAdditionalNetworksRow: false,
                            sortQuery: {
                                field: AssetColumns.totalAssetValueDollar,
                                direction: SortDirections.desc,
                            },
                        }}
                    />
                </PageWrapper>
            </SidebarLayout>
        </PageTrack>
    );
}
